import { useForm } from "@tanstack/react-form";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { Button, IconButton } from "../../components/Button";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { Input } from "../../components/Input";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { FormActions } from "../../components/FormActions";
import { Form } from "../../components/Form";
import { InfoBox } from "../../components/InfoBox";
import { SelectInput } from "../../components/SelectInput";
import { Toggle } from "../../components/Toggle";
import type { RestChain } from "@KassaroAB/shared/types/chain";
import type { ExtraProductField } from "@KassaroAB/shared/types/extraProductField";
import { ExtraProductFieldType } from "@KassaroAB/shared/types/enums/ExtraProductFieldType";

type ExtraProductFieldFormValue = {
  key: string;
  name: string;
  type: ExtraProductFieldType;
  showOnReceipt: boolean;
};

const ErrorMessage = styled.div`
  color: ${colors.danger};
  font-size: ${fontSizes.small}px;
  margin-top: ${spacing.x05}px;
`;

const FieldList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${spacing.x2}px;
  padding: ${spacing.x2}px;
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
`;

const FieldNameGroup = styled(FormGroup)`
  flex: 1;
  min-width: 0;
`;

const VAT_OPTIONS = [25, 12, 6, 0];

const toFormFields = (
  extraProductFields: ExtraProductField[] | null | undefined,
): ExtraProductFieldFormValue[] =>
  (extraProductFields ?? []).map((field) => ({
    key: field.key,
    name: field.name,
    type: field.type,
    showOnReceipt: field.showOnReceipt ?? true,
  }));

const toSavedFields = (
  extraProductFields: ExtraProductFieldFormValue[],
): ExtraProductField[] =>
  extraProductFields
    .filter((field) => field.name.trim() !== "")
    .map((field) => ({
      name: field.name.trim(),
      type: ExtraProductFieldType.TEXT,
      key: field.key || field.name.trim().toLowerCase().replace(/ /g, "_"),
      showOnReceipt: field.showOnReceipt,
    }));

type ChainCatalogSettingsFormProps = {
  chain: RestChain;
  onSubmit: (values: {
    defaultVAT: number;
    extraProductFields: ExtraProductField[];
  }) => void;
  isLoading?: boolean;
  formKey?: string;
  errorMessage?: string;
};

export const ChainCatalogSettingsForm = ({
  chain,
  onSubmit,
  isLoading,
  formKey,
  errorMessage,
}: ChainCatalogSettingsFormProps) => {
  const { t } = useTranslation();

  const form = useForm({
    defaultValues: {
      defaultVAT: chain.defaultVAT ?? 25,
      extraProductFields: toFormFields(chain.extraProductFields),
    },
    onSubmit: ({ value }) => {
      onSubmit({
        defaultVAT: value.defaultVAT,
        extraProductFields: toSavedFields(value.extraProductFields),
      });
    },
  });

  return (
    <Form
      key={formKey}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      {errorMessage ? <InfoBox variant="error">{errorMessage}</InfoBox> : null}

      <form.Field name="defaultVAT">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("chainSettings.defaultVAT")}</FormLabel>
            <SelectInput
              value={String(field.state.value)}
              onChange={(e) => field.handleChange(Number(e.target.value))}
              onBlur={field.handleBlur}
            >
              {VAT_OPTIONS.map((vat) => (
                <option key={vat} value={vat}>
                  {vat}%
                </option>
              ))}
            </SelectInput>
          </FormGroup>
        )}
      </form.Field>

      <form.Field name="extraProductFields">
        {(field) => {
          const extraProductFields = field.state.value;
          return (
            <FormGroup>
              <FormLabel>{t("chainSettings.extraProductFieldsLabel")}</FormLabel>
              <FieldList>
                {extraProductFields.map((_extraField, index) => (
                  <FieldRow key={index}>
                    <form.Field
                      name={`extraProductFields[${index}].name`}
                      validators={{
                        onChange: ({ value }) =>
                          !value?.trim()
                            ? t("chainSettings.extraProductFieldNameRequired")
                            : undefined,
                      }}
                    >
                      {(nameField) => (
                        <FieldNameGroup>
                          <FormLabel>
                            {t("chainSettings.extraProductFieldName")}
                          </FormLabel>
                          <Input
                            type="text"
                            value={nameField.state.value}
                            onChange={(e) =>
                              nameField.handleChange(e.target.value)
                            }
                            onBlur={nameField.handleBlur}
                            placeholder={t(
                              "chainSettings.extraProductFieldNamePlaceholder",
                            )}
                          />
                          {nameField.state.meta.errors.length > 0 ? (
                            <ErrorMessage>
                              {nameField.state.meta.errors[0]}
                            </ErrorMessage>
                          ) : null}
                        </FieldNameGroup>
                      )}
                    </form.Field>
                    <form.Field
                      name={`extraProductFields[${index}].showOnReceipt`}
                    >
                      {(showField) => (
                        <FormGroup>
                          <FormLabel>
                            {t("chainSettings.extraProductFieldShowOnReceipt")}
                          </FormLabel>
                          <Toggle
                            checked={showField.state.value}
                            onChange={showField.handleChange}
                            onBlur={showField.handleBlur}
                          />
                        </FormGroup>
                      )}
                    </form.Field>
                    <IconButton
                      type="button"
                      $color="danger"
                      onClick={() => {
                        field.handleChange(
                          extraProductFields.filter(
                            (_, fieldIndex) => fieldIndex !== index,
                          ),
                        );
                      }}
                      aria-label={t("common.delete")}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </FieldRow>
                ))}
              </FieldList>
              <Button
                type="button"
                $variant="outlined"
                $small
                onClick={() => {
                  field.handleChange([
                    ...extraProductFields,
                    {
                      key: "",
                      name: "",
                      type: ExtraProductFieldType.TEXT,
                      showOnReceipt: true,
                    },
                  ]);
                }}
              >
                <Plus size={16} />
                {t("chainSettings.addExtraProductField")}
              </Button>
            </FormGroup>
          );
        }}
      </form.Field>

      <FormActions>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? t("common.saving") : t("common.saveChanges")}
        </Button>
      </FormActions>
    </Form>
  );
};
